import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import Icon from '../../components/common/Icon';
import Button from '../../components/common/Button';
import { mediatorAPI, adminAPI } from '../../services/api';
import PaginationBar from '../../components/common/PaginationBar';

const AdminMediators = () => {
  const navigate = useNavigate();
  const [mediators, setMediators] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [pagination, setPagination] = useState({
    currentPage: 0,
    totalPages: 0,
    totalItems: 0,
    pageSize: 10,
    hasNext: false,
    hasPrevious: false
  });
  const [selectedMediator, setSelectedMediator] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  const [activeDropdown, setActiveDropdown] = useState(null);

  const fetchMediators = async (page = 0, term = '') => {
    setLoading(true);
    try {
      const response = await mediatorAPI.searchMediators(term, page, pagination.pageSize);
      const data = response.data;
      
      // Handle the actual API response structure
      if (data.data) {
        setMediators(data.data);
        setPagination(prev => ({
          ...prev,
          currentPage: data.pageNumber || 0,
          totalPages: data.totalPages || 0,
          totalItems: data.totalElements || 0,
          hasNext: data.hasNext || false,
          hasPrevious: data.hasPrevious || false
        }));
      } else {
        // Fallback for non-paginated response
        setMediators(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Failed to fetch mediators:', error);
      toast.error('Failed to load mediators');
      setMediators([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMediators();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchMediators(0, searchTerm);
  };

  const handlePageChange = (page) => {
    fetchMediators(page, searchTerm);
  };

  const handleView = async (mediator) => {
    try {
      const response = await mediatorAPI.getMediator(mediator.id);
      setSelectedMediator(response.data.data); // Fix: Access data.data
      setShowViewModal(true);
    } catch (error) {
      toast.error('Failed to fetch mediator details');
    }
  };

  const handleEdit = async (mediator) => {
    try {
      const response = await mediatorAPI.getMediator(mediator.id);
      setSelectedMediator(response.data.data); // Fix: Access data.data
      setEditFormData(response.data.data);
      setShowEditModal(true);
    } catch (error) {
      toast.error('Failed to fetch mediator details');
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      await mediatorAPI.updateMediator(selectedMediator.id, editFormData);
      toast.success('Mediator updated successfully');
      setShowEditModal(false);
      fetchMediators(pagination.currentPage, searchTerm);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update mediator');
    }
  };

  const handleDelete = async () => {
    try {
      await mediatorAPI.deleteMediator(selectedMediator.id);
      toast.success('Mediator deleted successfully');
      setShowDeleteModal(false);
      fetchMediators(pagination.currentPage, searchTerm);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete mediator');
    }
  };

  const handleToggleStatus = async (mediator) => {
    try {
      await mediatorAPI.toggleMediatorStatus(mediator.id);
      const newStatus = !mediator.active;
      toast.success(`Mediator ${newStatus ? 'activated' : 'deactivated'} successfully`);
      fetchMediators(pagination.currentPage, searchTerm);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to toggle status');
    }
  };

  const handleUnlock = async (mediator) => {
    try {
      await adminAPI.unlockMediator(mediator.id);
      toast.success(`Login unlocked for ${mediator.mediatorName}`);
      fetchMediators(pagination.currentPage, searchTerm);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to unlock mediator login');
    }
  };

  const handleDropdownToggle = (mediatorId) => {
    setActiveDropdown(activeDropdown === mediatorId ? null : mediatorId);
  };

  const handleDropdownAction = (action, mediator) => {
    setActiveDropdown(null);
    switch(action) {
      case 'view':
        handleView(mediator);
        break;
      case 'edit':
        handleEdit(mediator);
        break;
      case 'delete':
        setSelectedMediator(mediator);
        setShowDeleteModal(true);
        break;
      case 'toggle':
        handleToggleStatus(mediator);
        break;
      case 'unlock':
        handleUnlock(mediator);
        break;
      default:
        break;
    }
  };

  const getStatusBadge = (active) => {
    return active ? 
      { color: '#10b981', bg: '#d1fae5', label: 'Active' } : 
      { color: '#6b7280', bg: '#f3f4f6', label: 'Inactive' };
  };

  const Pagination = () => (
    <PaginationBar
      page={pagination.currentPage}
      totalPages={pagination.totalPages}
      totalElements={pagination.totalItems}
      pageSize={pagination.pageSize}
      onPageChange={handlePageChange}
      style={{ padding: '20px 24px', borderTop: '1px solid var(--border)', background: 'var(--bg)' }}
    />
  );

  return (
    <div className="admin-page-shell" style={{ 
      padding: '16px',
      flex: 1,
      backgroundColor: '#f8fafc',
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      height: '100vh',
      overflow: 'hidden',
      position: 'relative'
    }}>
      <div style={{ 
        flexShrink: 0,
        position: 'sticky',
        top: 0,
        zIndex: 10,
        backgroundColor: '#f8fafc',
        paddingBottom: '16px'
      }}>
        <div className="admin-page-header" style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          marginBottom: '16px'
        }}>
          <h2 style={{ fontSize: '20px', fontWeight: '700', color: 'var(--text)', margin: 0 }}>Mediator Management</h2>
          <Button 
            icon="plus"
            onClick={() => navigate('/admin/create-mediator')}
          >
            Add Mediator
          </Button>
        </div>

        <form onSubmit={handleSearch}>
          <div style={{ display: 'flex', gap: '12px', maxWidth: '500px' }}>
            <div style={{ flex: 1, position: 'relative' }}>
              <Icon 
                name="search" 
                size={16} 
                style={{ 
                  position: 'absolute', 
                  left: '12px', 
                  top: '50%', 
                  transform: 'translateY(-50%)',
                  color: 'var(--text-muted)'
                }} 
              />
              <input
                type="text"
                placeholder="Search by mediator name or code..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px 10px 40px',
                  border: '1px solid var(--border)',
                  borderRadius: '8px',
                  background: '#ffffff',
                  color: 'var(--text)',
                  fontSize: '14px'
                }}
              />
            </div>
            <Button type="submit" disabled={loading}>
              Search
            </Button>
          </div>
        </form>
      </div>
      
      <div className="admin-table-wrap" style={{ 
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column'
      }}>
        <div style={{ 
          background: '#ffffff', 
          borderRadius: '16px', 
          border: '1px solid #e2e8f0', 
          overflow: 'hidden',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          height: '100%'
        }}>
          <p className="mobile-scroll-hint">Swipe horizontally to see all columns</p>
          <div className="table-scroll-container" style={{ overflow: 'auto', flex: 1, minHeight: 0 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '800px' }}>
              <thead style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', position: 'sticky', top: 0, zIndex: 10 }}>
                <tr>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase' }}>Name</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase' }}>Code</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase' }}>Email</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase' }}>Phone</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase' }}>Status</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} style={{ padding: '80px 20px', textAlign: 'center' }}>
                    <div style={{ display: 'inline-block', width: '40px', height: '40px', border: '3px solid var(--bg-deep)', borderTop: '3px solid var(--primary)', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
                    <div style={{ marginTop: '16px', color: 'var(--text-muted)', fontSize: '14px' }}>Loading mediators...</div>
                  </td>
                </tr>
              ) : mediators.length === 0 ? (
                <tr>
                  <td colSpan={6} style={{ padding: '80px 20px', textAlign: 'center' }}>
                    <div style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.5 }}>👥</div>
                    <div style={{ fontSize: '16px', color: 'var(--text-muted)', marginBottom: '8px' }}>No mediators found</div>
                    <div style={{ fontSize: '14px', color: 'var(--text-muted)' }}>Try adjusting your search or add a new mediator</div>
                  </td>
                </tr>
              ) : (
                mediators.map((mediator) => {
                  const status = getStatusBadge(mediator.active);
                  return (
                    <tr 
                      key={mediator.id} 
                      style={{ 
                        borderBottom: '1px solid var(--border)',
                        transition: 'background-color 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = 'var(--bg-deep)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      <td style={{ padding: '16px 24px' }}>
                        <div style={{ fontWeight: '600', color: 'var(--text)', fontSize: '15px' }}>{mediator.mediatorName}</div>
                      </td>
                      <td style={{ padding: '16px 24px' }}>
                        <div style={{ fontSize: '14px', color: 'var(--text-sub)', fontWeight: '500' }}>{mediator.mediatorCode}</div>
                      </td>
                      <td style={{ padding: '16px 24px' }}>
                        <div style={{ fontSize: '14px', color: 'var(--text-sub)' }}>{mediator.email || '-'}</div>
                      </td>
                      <td style={{ padding: '16px 24px' }}>
                        <div style={{ fontSize: '14px', color: 'var(--text-sub)' }}>{mediator.phoneNumber}</div>
                      </td>
                      <td style={{ padding: '16px 24px' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', alignItems: 'flex-start' }}>
                          <span style={{
                            padding: '6px 12px',
                            borderRadius: '8px',
                            fontSize: '12px',
                            fontWeight: '600',
                            background: status.bg,
                            color: status.color
                          }}>
                            {status.label}
                          </span>
                          {mediator.accountLocked && (
                            <span style={{
                              padding: '4px 8px',
                              borderRadius: '8px',
                              fontSize: '11px',
                              fontWeight: '600',
                              background: '#fee2e2',
                              color: '#b91c1c'
                            }}>
                              Login Locked
                            </span>
                          )}
                        </div>
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                          {mediator.accountLocked && (
                            <button
                              onClick={() => handleUnlock(mediator)}
                              style={{
                                padding: '6px 10px',
                                background: '#fef3c7',
                                color: '#92400e',
                                border: '1px solid #fde68a',
                                borderRadius: '6px',
                                fontSize: '12px',
                                fontWeight: '600',
                                cursor: 'pointer',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              Unlock
                            </button>
                          )}
                        <div style={{ position: 'relative' }}>
                          <button 
                            onClick={() => handleDropdownToggle(mediator.id)} 
                            style={{ 
                              padding: '6px 12px', 
                              background: '#3b82f6', 
                              color: 'white', 
                              border: 'none', 
                              borderRadius: '6px', 
                              fontSize: '12px', 
                              cursor: 'pointer', 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: '4px',
                              transition: 'background 0.2s ease'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = '#2563eb'}
                            onMouseLeave={(e) => e.currentTarget.style.background = '#3b82f6'}
                          >
                            More
                            <Icon name="chevron-down" size={14} />
                          </button>
                          {activeDropdown === mediator.id && (
                            <div style={{ 
                              position: 'absolute', 
                              left: 0, 
                              top: '100%', 
                              marginTop: '4px',
                              background: '#ffffff', 
                              border: '1px solid #e2e8f0', 
                              borderRadius: '8px', 
                              zIndex: 20, 
                              boxShadow: '0 4px 6px rgba(0,0,0,0.1)', 
                              padding: '4px', 
                              minWidth: '120px' 
                            }}>
                              <button onClick={() => handleDropdownAction('view', mediator)} style={{ width: '100%', padding: '8px', border: 'none', background: 'none', textAlign: 'left', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <Icon name="eye" size={14} style={{ color: '#3b82f6', opacity: 0.8 }} /> View
                              </button>
                              <button onClick={() => handleDropdownAction('edit', mediator)} style={{ width: '100%', padding: '8px', border: 'none', background: 'none', textAlign: 'left', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <Icon name="edit-2" size={14} style={{ color: '#10b981', opacity: 0.8 }} /> Edit
                              </button>
                              {mediator.accountLocked && (
                                <button onClick={() => handleDropdownAction('unlock', mediator)} style={{ width: '100%', padding: '8px', border: 'none', background: 'none', textAlign: 'left', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', color: '#92400e' }}>
                                  <Icon name="refresh-cw" size={14} /> Unlock Login
                                </button>
                              )}
                              <div style={{ height: '1px', background: '#e2e8f0', margin: '4px 0' }} />
                              <button onClick={() => handleDropdownAction('delete', mediator)} style={{ width: '100%', padding: '8px', border: 'none', background: 'none', textAlign: 'left', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', color: '#ef4444' }}>
                                <Icon name="trash-2" size={14} /> Delete
                              </button>
                            </div>
                          )}
                        </div>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        
        {/* Pagination */}
        <div style={{ flexShrink: 0 }}>
          <Pagination />
        </div>
      </div>
    </div>

      {/* View Modal */}
      {showViewModal && selectedMediator && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: '#ffffff',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '500px',
            width: '90%',
            maxHeight: '80vh',
            overflowY: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '20px', fontWeight: '700', color: 'var(--text)' }}>Mediator Details</h3>
                <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: 'var(--text-muted)' }}>Viewing full information for this mediator</p>
              </div>
              <button
                onClick={() => setShowViewModal(false)}
                style={{ 
                  background: 'var(--bg-deep)', 
                  border: 'none', 
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer', 
                  color: 'var(--text-muted)',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => e.currentTarget.style.color = 'var(--red)'}
                onMouseLeave={(e) => e.currentTarget.style.color = 'var(--text-muted)'}
              >
                <Icon name="x" size={20} />
              </button>
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '20px' }}>
              <div style={{ padding: '16px', background: 'var(--bg-deep)', borderRadius: '12px' }}>
                <label style={{ fontSize: '11px', fontWeight: '700', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: '8px' }}>Name & Code</label>
                <div style={{ fontSize: '16px', color: 'var(--text)', fontWeight: '600' }}>{selectedMediator.mediatorName}</div>
                <div style={{ fontSize: '13px', color: 'var(--text-muted)', marginTop: '4px' }}>Code: {selectedMediator.mediatorCode}</div>
              </div>

              <div style={{ padding: '16px', background: 'var(--bg-deep)', borderRadius: '12px' }}>
                <label style={{ fontSize: '11px', fontWeight: '700', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: '8px' }}>Contact Info</label>
                <div style={{ fontSize: '14px', color: 'var(--text)', fontWeight: '500' }}>{selectedMediator.email || 'No email provided'}</div>
                <div style={{ fontSize: '14px', color: 'var(--text)', marginTop: '4px' }}>{selectedMediator.phoneNumber}</div>
              </div>

              <div style={{ padding: '16px', background: 'var(--bg-deep)', borderRadius: '12px', gridColumn: '1 / -1' }}>
                <label style={{ fontSize: '11px', fontWeight: '700', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: '8px' }}>Bank Details</label>
                <div style={{ fontSize: '14px', color: 'var(--text)', whiteSpace: 'pre-wrap', lineHeight: '1.6' }}>{selectedMediator.bankDetails || 'No bank details provided.'}</div>
              </div>

              <div style={{ padding: '16px', background: 'var(--bg-deep)', borderRadius: '12px' }}>
                <label style={{ fontSize: '11px', fontWeight: '700', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: '8px' }}>Status</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div style={{ 
                    width: '10px', 
                    height: '10px', 
                    borderRadius: '50%', 
                    background: selectedMediator.active ? '#10b981' : '#ef4444' 
                  }} />
                  <span style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: selectedMediator.active ? '#10b981' : '#ef4444'
                  }}>
                    {selectedMediator.active ? 'ACTIVE' : 'INACTIVE'}
                  </span>
                </div>
              </div>
            </div>
            
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px' }}>
              <Button
                variant="secondary"
                onClick={() => setShowViewModal(false)}
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedMediator && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: '#ffffff',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '500px',
            width: '90%',
            maxHeight: '80vh',
            overflowY: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: 'var(--text)' }}>Edit Mediator</h3>
              <button
                onClick={() => setShowEditModal(false)}
                style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: 'var(--text-muted)' }}
              >
                ×
              </button>
            </div>
            
            <form onSubmit={handleEditSubmit}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500', color: 'var(--text)' }}>Mediator Name *</label>
                  <input
                    type="text"
                    value={editFormData.mediatorName || ''}
                    onChange={(e) => setEditFormData({...editFormData, mediatorName: e.target.value})}
                    required
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid var(--border)',
                      borderRadius: '6px',
                      background: '#ffffff',
                      color: 'var(--text)',
                      fontSize: '14px'
                    }}
                  />
                </div>
                
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500', color: 'var(--text)' }}>Mediator Code *</label>
                  <input
                    type="text"
                    value={editFormData.mediatorCode || ''}
                    onChange={(e) => setEditFormData({...editFormData, mediatorCode: e.target.value})}
                    required
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid var(--border)',
                      borderRadius: '6px',
                      background: '#ffffff',
                      color: 'var(--text)',
                      fontSize: '14px'
                    }}
                  />
                </div>
                
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500', color: 'var(--text)' }}>Email</label>
                  <input
                    type="email"
                    value={editFormData.email || ''}
                    onChange={(e) => setEditFormData({...editFormData, email: e.target.value})}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid var(--border)',
                      borderRadius: '6px',
                      background: '#ffffff',
                      color: 'var(--text)',
                      fontSize: '14px'
                    }}
                  />
                </div>
                
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500', color: 'var(--text)' }}>Phone Number *</label>
                  <input
                    type="tel"
                    value={editFormData.phoneNumber || ''}
                    onChange={(e) => setEditFormData({...editFormData, phoneNumber: e.target.value})}
                    required
                    pattern="[0-9]{10}"
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid var(--border)',
                      borderRadius: '6px',
                      background: '#ffffff',
                      color: 'var(--text)',
                      fontSize: '14px'
                    }}
                  />
                </div>
                
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500', color: 'var(--text)' }}>Bank Details</label>
                  <textarea
                    value={editFormData.bankDetails || ''}
                    onChange={(e) => setEditFormData({...editFormData, bankDetails: e.target.value})}
                    rows={3}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid var(--border)',
                      borderRadius: '6px',
                      background: '#ffffff',
                      color: 'var(--text)',
                      fontSize: '14px',
                      resize: 'vertical'
                    }}
                  />
                </div>
                
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500', color: 'var(--text)' }}>Status</label>
                  <select
                    value={editFormData.active ? 'true' : 'false'}
                    onChange={(e) => setEditFormData({...editFormData, active: e.target.value === 'true'})}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid var(--border)',
                      borderRadius: '6px',
                      background: '#ffffff',
                      color: 'var(--text)',
                      fontSize: '14px'
                    }}
                  >
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                  </select>
                </div>
              </div>
              
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px' }}>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setShowEditModal(false)}
                >
                  Cancel
                </Button>
                <Button type="submit">
                  Save Changes
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedMediator && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: '#ffffff',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '400px',
            width: '90%'
          }}>
            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
              <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: '600', color: 'var(--text)' }}>Delete Mediator</h3>
              <p style={{ margin: 0, fontSize: '14px', color: 'var(--text-muted)' }}>
                Are you sure you want to delete <strong>{selectedMediator.mediatorName}</strong> ({selectedMediator.mediatorCode})? This action cannot be undone.
              </p>
            </div>
            
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <Button
                variant="secondary"
                onClick={() => setShowDeleteModal(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleDelete}
                style={{ background: 'var(--red)', borderColor: 'var(--red)' }}
              >
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminMediators;
